import React from 'react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import { Users, Wifi, WifiOff } from 'lucide-react';

const CollaboratorAvatars = ({ collaborators, isConnected, myColor }) => {
    if (!isConnected) {
        return (
            <div className="flex items-center gap-2 text-muted-foreground">
                <WifiOff className="h-4 w-4" />
                <span className="text-xs">Offline</span>
            </div>
        );
    }

    const totalCollaborators = collaborators.length;

    if (totalCollaborators === 0) {
        return (
            <TooltipProvider>
                <Tooltip>
                    <TooltipTrigger asChild>
                        <div className="flex items-center gap-2 text-muted-foreground cursor-default">
                            <Wifi className="h-4 w-4 text-green-500" />
                            <span className="text-xs">Editing solo</span>
                        </div>
                    </TooltipTrigger>
                    <TooltipContent>
                        <p>Real-time collaboration is active</p>
                        <p className="text-xs text-muted-foreground">Share the link to collaborate</p>
                    </TooltipContent>
                </Tooltip>
            </TooltipProvider>
        );
    }

    // Show up to 3 avatars, then "+N more"
    const visibleCollaborators = collaborators.slice(0, 3);
    const hiddenCount = totalCollaborators - 3;

    return (
        <TooltipProvider>
            <div className="flex items-center gap-2">
                <Wifi className="h-4 w-4 text-green-500" />
                <div className="flex -space-x-2">
                    {visibleCollaborators.map((collaborator) => (
                        <Tooltip key={collaborator.user_id}>
                            <TooltipTrigger asChild>
                                <div
                                    className="w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-medium border-2 border-background cursor-default"
                                    style={{ backgroundColor: collaborator.user_color }}
                                >
                                    {collaborator.user_name?.charAt(0).toUpperCase() || '?'}
                                </div>
                            </TooltipTrigger>
                            <TooltipContent>
                                <p className="font-medium">{collaborator.user_name}</p>
                                {collaborator.cursor_position !== null && (
                                    <p className="text-xs text-muted-foreground">
                                        Cursor at position {collaborator.cursor_position}
                                    </p>
                                )}
                            </TooltipContent>
                        </Tooltip>
                    ))}
                    {hiddenCount > 0 && (
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <div className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-medium border-2 border-background bg-muted text-muted-foreground cursor-default">
                                    +{hiddenCount}
                                </div>
                            </TooltipTrigger>
                            <TooltipContent>
                                <p>{hiddenCount} more collaborator{hiddenCount > 1 ? 's' : ''}</p>
                                {collaborators.slice(3).map(c => (
                                    <p key={c.user_id} className="text-xs">{c.user_name}</p>
                                ))}
                            </TooltipContent>
                        </Tooltip>
                    )}
                </div>
                <span className="text-xs text-muted-foreground">
                    {totalCollaborators} editing
                </span>
            </div>
        </TooltipProvider>
    );
};

// Component to show collaborator cursors in the editor
export const CollaboratorCursor = ({ collaborator, editorRef }) => {
    if (collaborator.cursor_position === null || collaborator.cursor_position === undefined) {
        return null;
    }

    // This would require deeper integration with the text editor
    // For now, just show a visual indicator
    return (
        <div 
            className="absolute w-0.5 h-5 animate-pulse"
            style={{ 
                backgroundColor: collaborator.user_color,
                left: `${collaborator.cursor_position}px` // Simplified positioning
            }}
        >
            <span 
                className="absolute -top-5 left-0 text-xs px-1 rounded whitespace-nowrap"
                style={{ backgroundColor: collaborator.user_color, color: 'white' }}
            >
                {collaborator.user_name}
            </span>
        </div>
    );
};

// Selection highlight overlay
export const CollaboratorSelection = ({ collaborator }) => {
    if (!collaborator.selection_start || !collaborator.selection_end) {
        return null;
    }

    return (
        <div 
            className="absolute pointer-events-none"
            style={{ 
                backgroundColor: `${collaborator.user_color}33`,
                border: `1px solid ${collaborator.user_color}`
            }}
        />
    );
};

export default CollaboratorAvatars;
